import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { request } from "../../../hooks/api";
import LogisticDetails from "./LogisticDetails";
import { Logistic, Location } from "src/services/types";
import AdminService from "src/services/admin.services";
import FoodLoader from "src/components/Loader";

const adminService = new AdminService();

const LogisticsWrapper: React.FC = () => {
  const [logistics, setLogistics] = useState<Logistic[]>([]);
  const [logisticsLoading, setLogisticsLoading] = useState<boolean>(false);
  const [logisticsError, setLogisticsError] = useState<string | null>(null);
  const [selectedLogistic, setSelectedLogistic] = useState<Logistic | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("trackingId");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [notifications, setNotifications] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locationData, setLocationData] = useState<Location | null>(null);

  useEffect(() => {
    const fetchLogistics = async () => {
      setLogisticsLoading(true);
      try {
        const response = await adminService.getLogistics(
          currentPage,
          searchTerm,
          sortField,
          sortOrder
        );
        setLogistics(response.data);
        setTotalPages(response.totalPages);
      } catch (err) {
        setLogisticsError("Error fetching logistics data");
      } finally {
        setLogisticsLoading(false);
      }
    };

    fetchLogistics();
  }, [currentPage, searchTerm, sortField, sortOrder]);

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSort = (field: string) => {
    setSortField(field);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 border rounded ${
            i === currentPage ? "bg-blue-500 text-white" : ""
          }`}
        >
          {i}
        </button>
      );
    }

    return pages;
  };

  if (logisticsLoading)
    return (
      <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Fetching Logistics History"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  if (logisticsError)
    return <div className="text-center text-red-500">{logisticsError}</div>;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <input
            type="text"
            placeholder="Search by Tracking ID or Cost"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2"
          />
        </div>
        <h2 className="text-2xl font-bold mb-4">Logistics History</h2>
        {logistics.length > 0 ? (
          <>
            <table className="min-w-full bg-white rounded-lg">
              <thead>
                <tr>
                  {["Tracking ID", "Date Delivered", "Cost", "Status"].map(
                    (header) => (
                      <th
                        key={header}
                        onClick={() =>
                          handleSort(header.toLowerCase().replace(" ", "_"))
                        }
                        className="px-5 py-3 text-left cursor-pointer"
                      >
                        {header}{" "}
                        {sortField === header.toLowerCase().replace(" ", "_") &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                    )
                  )}
                  <th className="px-5 py-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {logistics.map((logistic) => (
                  <tr
                    key={logistic._id}
                    className="hover:bg-gray-100 cursor-pointer"
                  >
                    <td className="px-5 py-3">{logistic.trackingId}</td>
                    <td className="px-5 py-3">
                      {new Date(logistic.delivered_time).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3">₦{logistic.cost.toFixed(2)}</td>
                    <td className="px-5 py-3">{logistic.order_status}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => setSelectedLogistic(logistic)}
                        className="text-blue-500"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-4 space-x-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded"
              >
                Previous
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded"
              >
                Last
              </button>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-500 mt-4">
            No logistics found
          </div>
        )}
      </div>

      {selectedLogistic && (
        <LogisticDetails
          logistic={selectedLogistic}
          onClose={() => setSelectedLogistic(null)}
          handleStatusChange={function (
            logistic: Logistic,
            newStatus: string
          ): void {
            throw new Error("Function not implemented.");
          }}
          successMessage={null}
          error={null}
          refetchSelectedOrder={() => {}}
        />
      )}
    </div>
  );
};

export default LogisticsWrapper;
